import { Box, Button, InputIcon } from '@platform/ui-library';
import {
  useEffect,
  useState
} from 'react';
import { useServices } from '../../hooks';
import {
  CheckOption,
  DesignIntent,
  Option
} from '../../types';
import styles from './generate-utterances.module.scss';
import { OnChangeUtterance } from './UtteranceList';
import { FormField, ItemDropdown } from '../../form_components';
import LoadingSpinner from '../../widgets/components/LoadingSpinner';

export interface GenerateUtterancesProps {
  intent: DesignIntent;
  isOpen: boolean;
  onChangeUtterance: OnChangeUtterance;
  onClose: () => void;
}

export function GenerateUtterances({
  intent,
  isOpen,
  onChangeUtterance,
  onClose
}: GenerateUtterancesProps) {
  const {
    errorMessageService,
    intentService,
    loadingService
  } = useServices();
  const [context, setContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [languageModel, setLanguageModel] = useState('');
  const [languageModelOptions, setLanguageModelOptions] = useState<Option<string>[]>([]);
  const [utterances, setUtterances] = useState<CheckOption[]>([]);

  useEffect(() => {
    loadingService.useLoading(async () => {
      const newLanguageModelOptions = await intentService.getLanguageModelOptions();
      setLanguageModelOptions(newLanguageModelOptions);
      setLanguageModel(newLanguageModelOptions[0].value);
    });
  }, []);

  const onGenerateUtterances = async () => {
    setIsGenerating(true);
    try {
      const utterances = await intentService.generatedUtterances({
        intent,
        context,
        provider: languageModel
      });
      setUtterances(utterances.map((label) => {
        return {
          checked: false,
          label
        };
      }));
    } catch (error) {
      errorMessageService.addError(error as Error);
    }

    setIsGenerating(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box className="slide-in flex-column" theme="noPadding">
      <div className="box flex-column header">
        <h2 className={styles.title}>Generate Utterances</h2>
        <h3 className={styles.description}>Edit and approve generated utterances before adding them to your intent.</h3>
      </div>
      <div className="flex-gap flex-grow">
        <div className="box content">
          <FormField label='LLM Provider'>
            <ItemDropdown
              onChange={setLanguageModel}
              items={languageModelOptions}
              required
              value={languageModel}
            />
          </FormField>
          <FormField label="LLM Context">
            <InputIcon
              placeholder={window.l.intents.generate_utterances_context_placeholder}
              onChange={(e) => setContext((e.target as HTMLInputElement).value)}
              type="textarea"
              style={{ minHeight: '7em' }}
              value={context}
            />
          </FormField>
          <div className="flex-end">
            <Button
              type='button'
              theme='default'
              style={{ zIndex: 0 }}
              onClick={onGenerateUtterances}
            >
              Generate
            </Button>
          </div>
          <div className="flex-grow" />
        </div>
        <div className={`${styles.utteranceContainer} flex-grow`}>
          {(isGenerating) ? (
            <LoadingSpinner
              className="flex-center flex-grow"
              message="Generating utterances..."
            />
          ) : (
            <div className="box content">
              {utterances.map(({ checked, label }, index) => {
                return (
                  <label className="clickable flex-gap" key={label}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setUtterances((utterances) => {
                          return utterances.map((
                            { checked, label },
                            mappedIndex
                          ) => {
                            return {
                              checked: (mappedIndex !== index) ? checked : !checked,
                              label
                            };
                          });
                        });
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="box footer">
        <Button
          type='button'
          theme="default"
          onClick={() => {
            if (!utterances.some((utterance) => utterance.checked)) {
              errorMessageService.addError('Please select utterances to add.');
              return;
            }

            utterances.filter(({ checked }) => {
              return checked;
            }).forEach(({ label }) => {
              onChangeUtterance({
                payload: { value: label },
                type: 'add'
              });
            });
            // Remove added utterances.
            setUtterances(utterances.filter(({ checked }) => {
              return !checked;
            }));
            onClose();
          }}
        >
          Add Selected Utterances
        </Button>
        <Button
          type='button'
          theme='outline'
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>
    </Box >
  );
}
